import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Zap, X, Loader2, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_FEATURES = [
  '1 workspace',
  '2 inbjudningar',
  'Kanban-board',
  'Quick Capture',
  'Taggar & filter',
];

const PRO_FEATURES = [
  'Obegränsat workspaces',
  'Obegränsade inbjudningar',
  'AI-assistent (GPT-5.2)',
  'Task breakdown & prioritering',
  'Daglig planeringshjälp',
  'Allt i Free-planen',
];

export function UpgradeModal() {
  const { user, refetchUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isPro = user?.plan === 'pro';

  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-upgrade', handler);
    return () => window.removeEventListener('open-upgrade', handler);
  }, []);

  // Poll for payment status on return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const upgrade = params.get('upgrade');
    
    if (sessionId && upgrade === 'success') {
      pollPaymentStatus(sessionId);
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('session_id');
      url.searchParams.delete('upgrade');
      window.history.replaceState({}, '', url.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) return;
    try {
      const { data } = await axios.get(`${API}/subscribe/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        if (refetchUser) await refetchUser();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/subscribe/checkout`, {
        origin_url: origin
      }, { withCredentials: true });
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-[#111113] border-white/10 text-zinc-50 max-w-lg p-0 overflow-hidden" data-testid="upgrade-modal">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold font-['Outfit'] flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              Uppgradera till Pro
            </DialogTitle>
          </DialogHeader>

          <div className="mt-6 grid grid-cols-2 gap-4">
            {/* Free Plan */}
            <div className="p-4 rounded-xl bg-[#09090B] border border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-zinc-400" />
                <h3 className="text-sm font-semibold text-zinc-300">Free</h3>
              </div>
              <p className="text-2xl font-bold text-zinc-100 font-['Outfit'] mb-4">0 kr<span className="text-xs text-zinc-500 font-normal">/mån</span></p>
              <ul className="space-y-2">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                    <Check className="w-3.5 h-3.5 text-zinc-600 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro Plan */}
            <div className="p-4 rounded-xl bg-gradient-to-b from-yellow-500/5 to-orange-500/5 border border-yellow-500/20 relative">
              <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-yellow-400 text-[10px] font-bold text-black">
                POPULÄR
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <h3 className="text-sm font-semibold text-yellow-400">Pro</h3>
              </div>
              <p className="text-2xl font-bold text-zinc-100 font-['Outfit'] mb-4">199 kr<span className="text-xs text-zinc-500 font-normal">/mån</span></p>
              <ul className="space-y-2">
                {PRO_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-200">
                    <Check className="w-3.5 h-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6">
            {isPro ? (
              <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-sm text-emerald-400 font-medium">Du har redan Pro-planen!</p>
              </div>
            ) : (
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 font-semibold"
                data-testid="upgrade-checkout-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Uppgradera till Pro — 199 kr/mån</>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
